/**
 * Client-side subscriber-state resolver.
 *
 * snippets/customer-state.liquid resolves `guest | prospect | active | lapsed`
 * on the server from the `stately.subscription` metafield / `sm:sub:*` tags.
 * Until the middleware writes those, every logged-in customer arrives as
 * `prospect`. This module asks the SBS middleware directly and upgrades that
 * answer to `active` or `lapsed` when it can prove it:
 *
 *   GET {statelyVars.api.public}/customer/{id}/subscriptions
 *       ?summary=true&status=active|cancelled&response=json
 *
 * Tri-state discipline (library/header/Header-Architecture.md): an API failure
 * is "unknown", never "not a subscriber". Unknown leaves the server state
 * alone and is not cached, so the next page load retries. Confirmed answers
 * are cached in sessionStorage per customer for CUSTOMER_STATE_TTL_MS.
 *
 * Pure module (no DOM) so the branching can be unit-tested with a fake fetch
 * and storage; header.js applies the result to the page.
 */

export const CUSTOMER_STATE_CACHE_KEY = "sm_customer_state";
export const CUSTOMER_STATE_TTL_MS = 5 * 60 * 1000;
// Per lookup. A stalled request is "unknown" like any other failure.
export const CUSTOMER_STATE_TIMEOUT_MS = 5000;

const RESOLVABLE_STATES = new Set(["prospect", "active", "lapsed"]);

/**
 * Does the customer have at least one subscription in `status`?
 *
 * @returns {Promise<boolean|null>} true / false when the API answered, null
 *   when it could not be reached or returned something unreadable.
 */
export async function hasSubscriptionWithStatus({
  apiBase,
  customerId,
  status,
  fetchImpl,
  timeoutMs = CUSTOMER_STATE_TIMEOUT_MS,
}) {
  const params = new URLSearchParams({ summary: "true", status, response: "json" });
  const url = `${apiBase}/customer/${encodeURIComponent(customerId)}/subscriptions?${params}`;

  // The deadline both aborts the real request and races it, so a fetch that
  // ignores the signal still settles, and a stalled lookup can never hold
  // back the other one's answer.
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      controller?.abort();
      reject(new Error("timeout"));
    }, timeoutMs);
  });

  try {
    const response = await Promise.race([fetchImpl(url, { signal: controller?.signal }), deadline]);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await Promise.race([response.json(), deadline]);
    // `data` is the response=json wrapper; the bare shape is kept as a fallback.
    const records = Object.values(payload?.data ?? payload ?? {}).filter(
      (record) => record && typeof record === "object"
    );
    // Re-check the status on the records rather than trusting the filter alone.
    return records.some((record) => record.status === status);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param {object} options
 * @param {string} options.apiBase        statelyVars.api.public
 * @param {Function} [options.fetchImpl]  fetch-compatible function
 * @param {Storage} [options.storage]     sessionStorage-compatible cache (optional)
 * @param {() => number} [options.now]    clock, for tests
 * @param {number} [options.ttl]          cache lifetime in ms
 * @param {number} [options.timeoutMs]    per-lookup deadline in ms
 * @param {Function} [options.log]        debug logger
 */
export function createCustomerStateResolver({
  apiBase,
  fetchImpl = globalThis.fetch,
  storage,
  now = Date.now,
  ttl = CUSTOMER_STATE_TTL_MS,
  timeoutMs = CUSTOMER_STATE_TIMEOUT_MS,
  log = () => {},
} = {}) {
  const readCache = (customerId) => {
    try {
      const cached = JSON.parse(storage?.getItem(CUSTOMER_STATE_CACHE_KEY) ?? "null");
      if (
        cached &&
        String(cached.customerId) === String(customerId) &&
        cached.expires > now() &&
        RESOLVABLE_STATES.has(cached.state)
      ) {
        return cached.state;
      }
    } catch {
      // Unreadable cache: fall through to the API.
    }
    return null;
  };

  const writeCache = (customerId, state) => {
    try {
      storage?.setItem(CUSTOMER_STATE_CACHE_KEY, JSON.stringify({ customerId, state, expires: now() + ttl }));
    } catch {
      // Storage unavailable: the answer simply is not remembered.
    }
  };

  const invalidate = () => {
    try {
      storage?.removeItem(CUSTOMER_STATE_CACHE_KEY);
    } catch {
      // Nothing to clear.
    }
  };

  const check = (customerId, status) =>
    hasSubscriptionWithStatus({ apiBase, customerId, status, fetchImpl, timeoutMs });

  /**
   * @param {{ serverState: string, customerId?: string|number }} input
   * @returns {Promise<{ state: string, source: 'server'|'cache'|'api'|'unknown' }>}
   */
  async function resolve({ serverState, customerId }) {
    // guest, active and lapsed are authoritative; only prospect is "unproven".
    if (serverState !== "prospect") return { state: serverState, source: "server" };
    if (!apiBase || !customerId) return { state: "prospect", source: "server" };

    const cached = readCache(customerId);
    if (cached) return { state: cached, source: "cache" };

    const [active, cancelled] = await Promise.all([check(customerId, "active"), check(customerId, "cancelled")]);

    let state;
    if (active === true) {
      state = "active";
    } else if (active === false && cancelled === true) {
      state = "lapsed";
    } else if (active === false && cancelled === false) {
      state = "prospect";
    } else {
      log("customer-state: subscription API unavailable, keeping server state", { active, cancelled });
      return { state: "prospect", source: "unknown" };
    }

    writeCache(customerId, state);
    return { state, source: "api" };
  }

  return { resolve, invalidate };
}
